import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { BarChart3, Download } from 'lucide-react';
import { sites, districts } from '../mock';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const SiteComparison = () => {
  const [selectedDistrict, setSelectedDistrict] = useState('all');
  
  const filteredSites = selectedDistrict === 'all' 
    ? sites 
    : sites.filter(s => s.district === selectedDistrict);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#0a3d62' }}>Site Comparison Report</h1>
          <p className="text-base mt-2" style={{ color: '#576574' }}>Comparative analysis of site performance</p>
        </div>
        <Button style={{ backgroundColor: '#10ac84' }}>
          <Download className="w-4 h-4 mr-2" />
          Export Report
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Select value={selectedDistrict} onValueChange={setSelectedDistrict}>
                <SelectTrigger>
                  <SelectValue placeholder="Select district" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Districts</SelectItem>
                  {districts.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Badge variant="outline" className="px-4 py-2">
              Comparing {filteredSites.length} sites
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Performance Comparison Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Performance Ratio (PR) Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <Bar
                data={{
                  labels: filteredSites.map(site => site.id),
                  datasets: [{
                    label: 'PR (%)',
                    data: filteredSites.map(site => site.pr),
                    backgroundColor: 'rgba(243, 156, 18, 0.8)',
                    borderColor: '#f39c12',
                    borderWidth: 1,
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false,
                    },
                    tooltip: {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      titleColor: '#fff',
                      bodyColor: '#fff',
                      callbacks: {
                        title: function(context) {
                          const site = filteredSites[context[0].dataIndex];
                          return `${site.id} - ${site.name}`;
                        }
                      }
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      max: 100,
                      grid: {
                        color: 'rgba(0, 0, 0, 0.1)',
                      },
                      ticks: {
                        color: '#576574',
                      }
                    },
                    x: {
                      grid: {
                        color: 'rgba(0, 0, 0, 0.1)',
                      },
                      ticks: {
                        color: '#576574',
                      }
                    }
                  }
                }}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Capacity Utilization Factor (CUF) Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <Bar
                data={{
                  labels: filteredSites.map(site => site.id),
                  datasets: [{
                    label: 'CUF (%)',
                    data: filteredSites.map(site => site.cuf),
                    backgroundColor: 'rgba(155, 89, 182, 0.8)',
                    borderColor: '#9b59b6',
                    borderWidth: 1,
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false,
                    },
                    tooltip: {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      titleColor: '#fff',
                      bodyColor: '#fff',
                      callbacks: {
                        title: function(context) {
                          const site = filteredSites[context[0].dataIndex];
                          return `${site.id} - ${site.name}`;
                        }
                      }
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      max: 25,
                      grid: {
                        color: 'rgba(0, 0, 0, 0.1)',
                      },
                      ticks: {
                        color: '#576574',
                      }
                    },
                    x: {
                      grid: {
                        color: 'rgba(0, 0, 0, 0.1)',
                      },
                      ticks: {
                        color: '#576574',
                      }
                    }
                  }
                }}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Comparison Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" style={{ color: '#3498db' }} />
            Performance Comparison
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b" style={{ backgroundColor: '#f5f7fa' }}>
                  <th className="text-left p-4 font-semibold" style={{ color: '#576574' }}>Rank</th>
                  <th className="text-left p-4 font-semibold" style={{ color: '#576574' }}>Site ID</th>
                  <th className="text-left p-4 font-semibold" style={{ color: '#576574' }}>Site Name</th>
                  <th className="text-left p-4 font-semibold" style={{ color: '#576574' }}>District</th>
                  <th className="text-left p-4 font-semibold" style={{ color: '#576574' }}>Capacity</th>
                  <th className="text-left p-4 font-semibold" style={{ color: '#576574' }}>Today Gen</th>
                  <th className="text-left p-4 font-semibold" style={{ color: '#576574' }}>PR</th>
                  <th className="text-left p-4 font-semibold" style={{ color: '#576574' }}>CUF</th>
                  <th className="text-left p-4 font-semibold" style={{ color: '#576574' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {[...filteredSites]
                  .sort((a, b) => b.pr - a.pr)
                  .map((site, idx) => (
                    <tr key={site.id} className="border-b hover:bg-gray-50 transition-colors">
                      <td className="p-4">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm" style={{ backgroundColor: idx < 3 ? '#10ac84' : '#f5f7fa', color: idx < 3 ? 'white' : '#576574' }}>
                          {idx + 1}
                        </div>
                      </td>
                      <td className="p-4 font-mono text-sm">{site.id}</td>
                      <td className="p-4 font-medium">{site.name}</td>
                      <td className="p-4 capitalize">{districts.find(d => d.id === site.district)?.name}</td>
                      <td className="p-4">{site.capacity} MW</td>
                      <td className="p-4 font-semibold">{site.todayGen} kWh</td>
                      <td className="p-4">
                        <Badge variant="outline" style={{ backgroundColor: site.pr >= 75 ? '#e8f5f1' : site.pr >= 60 ? '#fff4e6' : '#fee', color: site.pr >= 75 ? '#10ac84' : site.pr >= 60 ? '#f39c12' : '#e74c3c' }}>
                          {site.pr}%
                        </Badge>
                      </td>
                      <td className="p-4">{site.cuf}%</td>
                      <td className="p-4">
                        <Badge variant="outline" style={{ backgroundColor: site.status === 'active' ? '#e8f5f1' : '#fee', color: site.status === 'active' ? '#10ac84' : '#e74c3c' }}>
                          {site.status.toUpperCase()}
                        </Badge>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-sm mb-2" style={{ color: '#576574' }}>Highest PR</div>
            <div className="text-2xl font-bold" style={{ color: '#10ac84' }}>{Math.max(...filteredSites.map(s => s.pr))}%</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-sm mb-2" style={{ color: '#576574' }}>Lowest PR</div>
            <div className="text-2xl font-bold" style={{ color: '#e74c3c' }}>{Math.min(...filteredSites.map(s => s.pr))}%</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-sm mb-2" style={{ color: '#576574' }}>Average PR</div>
            <div className="text-2xl font-bold" style={{ color: '#3498db' }}>
              {(filteredSites.reduce((sum, s) => sum + s.pr, 0) / filteredSites.length).toFixed(1)}%
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-sm mb-2" style={{ color: '#576574' }}>Average CUF</div>
            <div className="text-2xl font-bold" style={{ color: '#9b59b6' }}>
              {(filteredSites.reduce((sum, s) => sum + s.cuf, 0) / filteredSites.length).toFixed(1)}%
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SiteComparison;
